import styled from '@emotion/styled'
import { Box, Typography } from '@mui/material'
import React from 'react'

import { getUrlByDomain } from '@js/common/getUrlByDomain'

import Modal from '@f/components/Modal/Modal'

import MonthlyRateCalculatorModal from '@f-fertighaus/applications/MonthlyRateCalculatorModal/MonthlyRateCalculatorModal'
import { CTAButton } from '@f-fertighaus/applications/Next/components/common/CTAButton'
import { DownIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/DownIcon'
import { HouseInHandIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/HouseInHandIcon'
import { MoneyFillIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/MoneyFillIcon'
import { InfoTooltip } from '@f-fertighaus/applications/Next/components/fertighaus/InfoTooltip/InfoTooltip'
import { Domain } from '@f-fertighaus/applications/Next/pages/house-detail-page/HDPContent'
import {
  DictionaryKey,
  t
} from '@f-fertighaus/applications/Next/public/locales/de'
import { EXPANSION_STAGE_BY_DISPLAY_NAME } from '@f-fertighaus/applications/Next/utils/backendStructureMappings'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { PRICE } from '@f-fertighaus/applications/Next/utils/price'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'

export const openMonthlyCalculator = ({
  price,
  expansionStage,
  domain
}: {
  price: number
  expansionStage: string
  domain: Domain
}) => {
  const modal = new Modal()

  modal.openModal({
    component: (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <MonthlyRateCalculatorModal
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        {...window.SherlockContainer.BudgetCalculator}
        housePrice={Number(price)}
        expansionStage={expansionStage}
        isModal
        domain={domain}
      />
    )
  })
}

export const PricesList = () => {
  const {
    state: {
      expansion_stage_display,
      price_fitting_display,
      price_lego_display,
      price_display
    },
    domain
  } = useHouseDetail()

  const getUrl = getUrlByDomain(domain)

  const mainExpansionStageType =
    EXPANSION_STAGE_BY_DISPLAY_NAME[expansion_stage_display]

  const handleMonthlyRateButtonClick = () => {
    if (price_display) {
      openMonthlyCalculator({
        price: price_display,
        expansionStage: expansion_stage_display,
        domain
      })
    }
    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'monthly-rate',
      location: 'price-section',
      values: [
        {
          type: 'text',
          value: t('houseDetailPage.sections.price.monthlyRateTitle')
        }
      ]
    })
  }

  const handleFinanceAssistanceCtaClick = () => {
    webtrack({
      category: 'CTA-link',
      action: 'click',
      label: 'finance-offer-comparison',
      indicates: ['funnel-entry'],
      location: 'price-section',
      values: [
        {
          type: 'text',
          value: t('houseDetailPage.sections.price.financeAssistanceButton')
        },
        { type: 'url', value: getUrl('financeAssistance')() }
      ]
    })
    window.open(getUrl('financeAssistance')(), '_blank')
  }

  return (
    <>
      <PriceDisplayWrapper>
        <IconContainer>
          <HouseInHandIcon />
        </IconContainer>
        <Box sx={{ flex: 1 }}>
          {mainExpansionStageType && (
            <TextWithTooltipWrapper>
              <Typography data-sel='house-expstg' variant='body1'>
                {t(
                  `houseDetailPage.sections.price.${mainExpansionStageType}PriceFrom` as DictionaryKey
                )}
              </Typography>
              <InfoTooltip
                title={t('houseDetailPage.sections.price.tooltip')}
                wtPayload={{ label: 'price', location: 'price-section' }}
              />
            </TextWithTooltipWrapper>
          )}
          <Typography
            data-sel='house-price'
            variant='h1'
            component='p'
            sx={{ marginBottom: 1 }}>
            {price_display
              ? PRICE.formatPrice(price_display)
              : t('houseDetailPage.houseOverview.price-missing')}
          </Typography>
          {price_lego_display && mainExpansionStageType !== 'lego' && (
            <TextWithTooltipWrapper>
              <Typography variant='body2' component='p'>
                {t('houseDetailPage.sections.price.legoPriceFrom')}
                <PriceValue>{PRICE.formatPrice(price_lego_display)}</PriceValue>
              </Typography>
              <InfoTooltip
                title={t(
                  'houseDetailPage.sections.details.overview.tooltips.expansion_stage.lego'
                )}
                wtPayload={{ label: 'lego', location: 'price-section' }}
              />
            </TextWithTooltipWrapper>
          )}
          {price_fitting_display && mainExpansionStageType !== 'fitting' && (
            <TextWithTooltipWrapper>
              <Typography variant='body2' component='p'>
                {t('houseDetailPage.sections.price.fittingPriceFrom')}
                <PriceValue>
                  {PRICE.formatPrice(price_fitting_display)}
                </PriceValue>
              </Typography>
              <InfoTooltip
                title={t(
                  'houseDetailPage.sections.details.overview.tooltips.expansion_stage.fitting'
                )}
                wtPayload={{
                  label: 'fitting',
                  location: 'price-section'
                }}
              />
            </TextWithTooltipWrapper>
          )}
        </Box>
      </PriceDisplayWrapper>
      {price_display && (
        <PriceDisplayWrapper>
          <IconContainer>
            <MoneyFillIcon />
          </IconContainer>
          <Box sx={{ flex: 1 }}>
            <ButtonWithArrow
              data-sel='btn-monthlyrate'
              variant='text'
              color='secondary'
              endIcon={<DownIcon />}
              onClick={handleMonthlyRateButtonClick}>
              {t('houseDetailPage.sections.price.monthlyRateTitle')}
            </ButtonWithArrow>
            <Typography
              suppressHydrationWarning
              variant='h1'
              component='p'
              sx={{ marginBottom: 1 }}>
              {t('houseDetailPage.sections.price.monthlyRatePrice', {
                price: PRICE.formatPrice(
                  PRICE.calculateMonthlyRate(price_display)
                )
              })}
            </Typography>
          </Box>
        </PriceDisplayWrapper>
      )}
      <FinanceAssistantButton
        variant='text'
        color='secondary'
        endIcon={<DownIcon />}
        onClick={handleFinanceAssistanceCtaClick}>
        {t('houseDetailPage.sections.price.financeAssistanceButton')}
      </FinanceAssistantButton>
    </>
  )
}

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.palette.primary.main};
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin-right: ${({ theme }) => theme.spacing(3)};

  svg {
    width: 40px;
    height: 40px;
  }
`

const PriceDisplayWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  margin-left: 1vw;

  :first-of-type {
    margin-bottom: ${({ theme }) => theme.spacing(3)};
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-left: 1.5vw;
  }
`

const PriceValue = styled.span`
  margin-left: ${({ theme }) => theme.spacing(2)};
`

const ButtonWithArrow = styled(CTAButton)`
  && {
    padding: 0;
    font-size: 14px;
    font-weight: normal;

    svg {
      transform: rotate(-90deg);
      width: 9px;

      * {
        stroke: ${({ theme }) => theme.palette.text.primary};
      }
    }
  }
`

const FinanceAssistantButton = styled(ButtonWithArrow)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: calc(${({ theme }) => theme.spacing(10.5)} + 1.5vw);
  }
`

const TextWithTooltipWrapper = styled(Box)`
  display: flex;
  align-items: center;
`
